import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  FormControl, InputLabel, Select, MenuItem, TextField, Stack, Alert, Typography,
} from '@mui/material';
import { api, isAuthenticated, API_URL } from '../utils/api';

export type ReportContentType = 'photo' | 'community_post' | 'profile' | 'other';

interface ReportContentModalProps {
  open: boolean;
  onClose: () => void;
  contentType: ReportContentType;
  contentId: string;
  contentLabel?: string; // optional human hint shown in the title (e.g. "this photo")
}

const REASONS: { code: string; label: string }[] = [
  { code: 'csam', label: 'Child sexual abuse or exploitation' },
  { code: 'sexual', label: 'Nudity or sexual content' },
  { code: 'harassment', label: 'Harassment or bullying' },
  { code: 'hate', label: 'Hate speech' },
  { code: 'violence', label: 'Violence or threats' },
  { code: 'illegal', label: 'Illegal content or activity' },
  { code: 'spam', label: 'Spam or scam' },
  { code: 'other', label: 'Something else' },
];

const ReportContentModal: React.FC<ReportContentModalProps> = ({
  open, onClose, contentType, contentId, contentLabel,
}) => {
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const reset = () => { setReason(''); setDetail(''); setError(null); setDone(false); setSubmitting(false); };
  const handleClose = () => { if (!submitting) { reset(); onClose(); } };

  const ANON_ALLOWED = ['csam', 'illegal'];

  const submit = async () => {
    if (!reason) { setError('Please choose a reason.'); return; }

    const authed = isAuthenticated();

    // Logged-out users can only report illegal content (csam/illegal) anonymously.
    // Any other reason requires an account.
    if (!authed && !ANON_ALLOWED.includes(reason)) {
      setError('Please log in to report this. Illegal-content reports can be made without an account.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (authed) {
        // Authenticated path — attributed report, all reasons.
        const res = await api.post('/content-reports', {
          content_type: contentType,
          content_id: contentId,
          reason_code: reason,
          reason_detail: detail.trim() || undefined,
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || 'Could not submit report');
      } else {
        // Anonymous path — bare fetch (NOT apiRequest) so a non-200 never triggers
        // the global logout/login bounce. No Authorization header.
        const res = await fetch(`${API_URL}/public/content-reports`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content_type: contentType,
            content_id: contentId,
            reason_code: reason,
            reason_detail: detail.trim() || undefined,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) throw new Error(data.error || 'Could not submit report');
      }
      setDone(true);
    } catch (e: any) {
      setError(e.message || 'Could not submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth container={() => document.fullscreenElement as HTMLElement || document.body}>
      <DialogTitle>Report {contentLabel || 'content'}</DialogTitle>
      <DialogContent dividers>
        {done ? (
          <Typography variant="body2">
            Thanks &mdash; our team will review this report. We don&apos;t share the outcome or who reported it.
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Tell us what&apos;s wrong. Reports are confidential &mdash; the person who posted this won&apos;t see that you reported it.
            </Typography>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            <FormControl size="small" fullWidth>
              <InputLabel>Reason</InputLabel>
              <Select label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} MenuProps={{ disablePortal: true }}>
                {REASONS.map((r) => <MenuItem key={r.code} value={r.code}>{r.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Details (optional)"
              multiline
              minRows={2}
              fullWidth
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              inputProps={{ maxLength: 2000 }}
              placeholder="Add anything that helps us review this"
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {done ? (
          <Button onClick={handleClose} variant="contained" sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#1F4E4A' } }}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={submitting || !reason}
              variant="contained"
              sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#1F4E4A' } }}
            >
              {submitting ? 'Submitting\u2026' : 'Submit report'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportContentModal;
